import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { AuthState, Organization, Permission, Store, User } from '@/libs/interfaces'

const safeJsonParse = <T>(value: string | null, fallback: T): T => {
  if (!value || value === 'undefined' || value === 'null') {
    return fallback
  }
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

const storedUser = localStorage.getItem('user')
const storedAuth = localStorage.getItem('isAuthenticated')
const storedToken = localStorage.getItem('authToken')
const storedPermissions = localStorage.getItem('permissions')
const storedCurrentOrg = localStorage.getItem('currentOrganization')
const storedCurrentStore = localStorage.getItem('currentStore')

const initialState: AuthState = {
  user: safeJsonParse<User | null>(storedUser, null),
  token: storedToken && storedToken !== 'undefined' ? storedToken : null,
  permissions: safeJsonParse<Permission[]>(storedPermissions, []),
  isPermissionsLoaded: storedPermissions !== null,
  currentOrganization: safeJsonParse<Organization | null>(storedCurrentOrg, null),
  currentStore: safeJsonParse<Store | null>(storedCurrentStore, null),
  isAuthenticated: storedAuth === 'true',
  isLoading: false,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload
      state.isAuthenticated = !!action.payload
      if (action.payload) {
        localStorage.setItem('user', JSON.stringify(action.payload))
        localStorage.setItem('isAuthenticated', 'true')
        // Set first organization as current if none selected
        if (action.payload.organizations?.length && !state.currentOrganization) {
          const firstOrg = action.payload.organizations[0]
          state.currentOrganization = firstOrg
          localStorage.setItem('currentOrganization', JSON.stringify(firstOrg))
        }
      } else {
        localStorage.removeItem('user')
        localStorage.setItem('isAuthenticated', 'false')
      }
    },
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload
      if (action.payload) {
        localStorage.setItem('authToken', action.payload)
      } else {
        localStorage.removeItem('authToken')
      }
    },
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload
      state.isPermissionsLoaded = true
      localStorage.setItem('permissions', JSON.stringify(action.payload))
    },
    setCurrentOrganization: (state, action: PayloadAction<Organization | null>) => {
      state.currentOrganization = action.payload
      if (action.payload)
        localStorage.setItem('currentOrganization', JSON.stringify(action.payload))
    },
    setCurrentStore: (state, action: PayloadAction<Store | null>) => {
      state.currentStore = action.payload
      if (action.payload) localStorage.setItem('currentStore', JSON.stringify(action.payload))
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.permissions = []
      state.isPermissionsLoaded = false
      state.currentOrganization = null
      state.currentStore = null
      state.isAuthenticated = false
      localStorage.clear()
    },
  },
})

export const {
  setUser,
  setToken,
  setPermissions,
  setCurrentOrganization,
  setCurrentStore,
  setLoading,
  logout,
} = authSlice.actions
export default authSlice.reducer
